import {offlineState} from '../lib/offlineVault';
import {useDeviceDraft,hasDeviceDraft} from '../lib/useDeviceDraft';
import { useQueryClient } from '@tanstack/react-query';
import { DateOfBirthFields } from '../components/DateOfBirthFields';
import { ageFromDob, todayDate, validateAncDetails } from '@pamosi/shared';
import { AncRegistrationFields } from '../components/AncRegistrationFields';
import { ClinicalNotesPanel } from '../components/ClinicalNotesPanel';
// Patient registration view — Phase 1b.2.
//
// Full Section A–F editor + HMO picker. The draft registration row was
// already created in Phase 1b when the Personal card was issued; this
// screen edits those fields in place and lets the receptionist mark each
// section complete + submit the whole form when all 6 are done.
//
// Field validation mirrors reception_router.ts PATCH endpoint. Dates are
// ISO; phones are E.164. Enum sets are inlined here to avoid shipping an
// extra bundle for them.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
    ClinicDepartment,
    HmoProvider,
    PatientHmoCoverage,
    PatientRegistrationDetails,
    ReceptionRegistrationBundleRes,
    ReceptionUpdateRegistrationReq,
    RegAlcohol,
    RegAllergyStatus,
    RegBloodGroupAbo,
    RegGender,
    RegGenotype,
    RegLivingSituation,
    RegMaritalStatus,
    RegRhFactor,
    RegSectionKey,
    RegSmoking,
} from '@pamosi/shared';
import { PATIENT_CARD_TYPE_LABELS, REG_SECTION_KEYS } from '@pamosi/shared';
import { ApiError, clinicApi, receptionApi } from '../lib/api';
import { SharedInstructionsPanel } from '../components/SharedInstructionsPanel';
import {
    openPatientCardWindow,
    openRegistrationSummaryWindow,
} from '../lib/printables';

const SECTION_LABELS: Record<RegSectionKey, string> = {
    personal: 'A — Personal',
    contact:  'B — Contact',
    socio:    'C — Socio-demographic',
    social:   'D — Social history',
    medical:  'E — Medical history',
    nok:      'F — Next of kin',
};

// Enum option arrays — mirror reception_router.ts whitelists.
const OPTS_GENDER:   RegGender[]          = ['male', 'female', 'other', 'prefer_not_to_say', 'unknown'];
const OPTS_MARITAL:  RegMaritalStatus[]   = ['single', 'married', 'divorced', 'widowed', 'separated', 'unknown'];
const OPTS_SMOKING:  RegSmoking[]         = ['never', 'former', 'current', 'unknown'];
const OPTS_ALCOHOL:  RegAlcohol[]         = ['never', 'occasional', 'regular', 'unknown'];
const OPTS_LIVING:   RegLivingSituation[] = ['alone', 'with_family', 'caregiver', 'other', 'unknown'];
const OPTS_ALLERGY:  RegAllergyStatus[]   = ['not_assessed', 'none_known', 'recorded', 'unknown'];
const OPTS_ABO:      RegBloodGroupAbo[]   = ['a', 'b', 'ab', 'o', 'unknown'];
const OPTS_RH:       RegRhFactor[]        = ['positive', 'negative', 'unknown'];
const OPTS_GENOTYPE: RegGenotype[]        = ['aa', 'as', 'ss', 'ac', 'sc', 'other', 'unknown'];
const OPTS_CHANNEL = ['sms', 'whatsapp', 'voice', 'in_person'] as const;

// Local form state mirrors the DB row's string/null fields with empty
// strings instead of nulls so the inputs stay controlled. We convert back
// to nulls when sending the PATCH.
type FormState = {
    [K in keyof PatientRegistrationDetails]:
        [PatientRegistrationDetails[K]] extends [string | null] ? string
        : [PatientRegistrationDetails[K]] extends [number | null] ? string
        : [PatientRegistrationDetails[K]] extends [boolean | null] ? boolean | null
        : PatientRegistrationDetails[K];
};

function toFormState(r: PatientRegistrationDetails): Partial<FormState> {
    const asStr = (v: string | null) => v ?? '';
    const asNum = (v: number | null) => (v === null ? '' : String(v));
    return {
        anc_details: r.anc_details ?? null,
        dob: asStr(r.dob),
        estimated_age_years: asNum(r.estimated_age_years),
        estimated_age_as_of: asStr(r.estimated_age_as_of),
        gender: asStr(r.gender),
        clinical_sex: asStr(r.clinical_sex),
        marital_status: asStr(r.marital_status),
        address: asStr(r.address),
        phone_e164_primary: asStr(r.phone_e164_primary),
        phone_e164_alt: asStr(r.phone_e164_alt),
        email: asStr(r.email),
        preferred_contact_channel: asStr(r.preferred_contact_channel),
        occupation: asStr(r.occupation),
        state_of_origin: asStr(r.state_of_origin),
        ethnicity: asStr(r.ethnicity),
        religion: asStr(r.religion),
        smoking: asStr(r.smoking),
        alcohol: asStr(r.alcohol),
        recreational_drugs: r.recreational_drugs,
        recreational_drugs_detail: asStr(r.recreational_drugs_detail),
        living_situation: asStr(r.living_situation),
        living_situation_detail: asStr(r.living_situation_detail),
        genotype: asStr(r.genotype),
        blood_group_abo: asStr(r.blood_group_abo),
        blood_group_rh: asStr(r.blood_group_rh),
        allergy_status: asStr(r.allergy_status),
        allergies_detail: asStr(r.allergies_detail),
        chronic_conditions_detail: asStr(r.chronic_conditions_detail),
        current_medicines_detail: asStr(r.current_medicines_detail),
        surgical_history_detail: asStr(r.surgical_history_detail),
        nok_name: asStr(r.nok_name),
        nok_phone_e164: asStr(r.nok_phone_e164),
        nok_relationship: asStr(r.nok_relationship),
        nok_address: asStr(r.nok_address),
        alt_emergency_contact_name: asStr(r.alt_emergency_contact_name),
        alt_emergency_contact_phone: asStr(r.alt_emergency_contact_phone),
    };
}

// Build a PATCH payload from the form state. Empty strings become null
// (clearing the field); numbers come across as parsed integers.
function toPatch(form: Partial<FormState>): ReceptionUpdateRegistrationReq {
    const out: ReceptionUpdateRegistrationReq = {};
    if (form.anc_details !== undefined) out.anc_details = form.anc_details;
    const assignStr = <K extends keyof ReceptionUpdateRegistrationReq>(k: K, v: string | undefined) => {
        if (v === undefined) return;
        (out as Record<string, unknown>)[k as string] = v.trim() === '' ? null : v.trim();
    };
    const assignEnum = <K extends keyof ReceptionUpdateRegistrationReq>(k: K, v: string | undefined) => {
        if (v === undefined) return;
        (out as Record<string, unknown>)[k as string] = v === '' ? null : v;
    };
    assignStr('dob', form.dob);
    if (form.estimated_age_years !== undefined) {
        const s = String(form.estimated_age_years).trim();
        out.estimated_age_years = s === '' ? null : Number(s);
    }
    assignStr('estimated_age_as_of', form.estimated_age_as_of);
    assignEnum('gender', form.gender);
    assignStr('clinical_sex', form.clinical_sex);
    assignEnum('marital_status', form.marital_status);
    assignStr('address', form.address);
    assignStr('phone_e164_primary', form.phone_e164_primary);
    assignStr('phone_e164_alt', form.phone_e164_alt);
    assignStr('email', form.email);
    assignEnum('preferred_contact_channel', form.preferred_contact_channel);
    assignStr('occupation', form.occupation);
    assignStr('state_of_origin', form.state_of_origin);
    assignStr('ethnicity', form.ethnicity);
    assignStr('religion', form.religion);
    assignEnum('smoking', form.smoking);
    assignEnum('alcohol', form.alcohol);
    if (form.recreational_drugs !== undefined) out.recreational_drugs = form.recreational_drugs ?? null;
    assignStr('recreational_drugs_detail', form.recreational_drugs_detail);
    assignEnum('living_situation', form.living_situation);
    assignStr('living_situation_detail', form.living_situation_detail);
    assignEnum('genotype', form.genotype);
    assignEnum('blood_group_abo', form.blood_group_abo);
    assignEnum('blood_group_rh', form.blood_group_rh);
    assignEnum('allergy_status', form.allergy_status);
    assignStr('allergies_detail', form.allergies_detail);
    assignStr('chronic_conditions_detail', form.chronic_conditions_detail);
    assignStr('current_medicines_detail', form.current_medicines_detail);
    assignStr('surgical_history_detail', form.surgical_history_detail);
    assignStr('nok_name', form.nok_name);
    assignStr('nok_phone_e164', form.nok_phone_e164);
    assignStr('nok_relationship', form.nok_relationship);
    assignStr('nok_address', form.nok_address);
    assignStr('alt_emergency_contact_name', form.alt_emergency_contact_name);
    assignStr('alt_emergency_contact_phone', form.alt_emergency_contact_phone);
    return out;
}

interface SectionProps {
    title: string;
    completed: boolean;
    onToggleComplete: (next: boolean) => void;
    children: React.ReactNode;
}

function Section({ title, completed, onToggleComplete, children }: SectionProps) {
    return (
        <div className="pm-card p-4 space-y-3">
            <div className="flex items-center justify-between">
                <div className="text-slate-700 text-sm font-semibold">{title}</div>
                <label className="flex items-center gap-2 text-xs text-slate-500">
                    <input
                        type="checkbox"
                        checked={completed}
                        onChange={(e) => onToggleComplete(e.target.checked)}
                    />
                    Mark section complete
                </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
        </div>
    );
}

interface FieldProps {
    label: string;
    children: React.ReactNode;
    full?: boolean;
}
function Field({ label, children, full }: FieldProps) {
    return (
        <label className={'block ' + (full ? 'md:col-span-2' : '')}>
            <span className="text-slate-500 text-xs">{label}</span>
            <div className="mt-1">{children}</div>
        </label>
    );
}

export function PatientRegistrationScreen() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { id } = useParams<{ id: string }>();

    const [bundle, setBundle] = useState<ReceptionRegistrationBundleRes | null>(null);
    const [clinicName, setClinicName] = useState<string>('Pamosi Clinic');
    const [loadError, setLoadError] = useState<string | null>(null);
    const [form, setForm] = useDeviceDraft<Partial<FormState>>('registration:'+id,{});
    const [completedSet, setCompletedSet] = useState<Set<RegSectionKey>>(new Set());

    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveNotice, setSaveNotice] = useState<string | null>(null);

    // HMO state
    const [hmoProviders, setHmoProviders] = useState<HmoProvider[]>([]);
    const [hmoSelected, setHmoSelected] = useState<string>('');
    const [hmoPolicy, setHmoPolicy] = useState('');
    const [hmoPlan, setHmoPlan] = useState('');
    const [hmoMember, setHmoMember] = useState('');
    const [hmoBusy, setHmoBusy] = useState(false);
    const [hmoError, setHmoError] = useState<string | null>(null);
    const [hmoCoverages, setHmoCoverages] = useState<PatientHmoCoverage[]>([]);

    // Send-to-department state (Phase 2a). Reception assigns the patient to
    // a clinic department after registering — this is the hand-off that
    // puts them on the clinical queue.
    const [departments, setDepartments] = useState<ClinicDepartment[]>([]);
    const [sendDeptId, setSendDeptId] = useState('');
    const [sendComplaint, setSendComplaint] = useState('');
    const [sendBusy, setSendBusy] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);
    const [sendNotice, setSendNotice] = useState<string | null>(null);

    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        setBundle(null);setLoadError(null);
        (async () => {
            try {
                const [res, providers, depts, me] = await Promise.all([
                    receptionApi.getRegistrationBundle(id),
                    receptionApi.listHmoProviders().catch(()=>({providers:[]})),
                    receptionApi.listDepartments(),
                    clinicApi.myClinic(),
                ]);
                if (cancelled) return;
                setBundle(res);
                setHmoCoverages(res.hmo_coverages);
                setHmoProviders(providers.providers);
                setDepartments(depts.departments);
                if (me.clinic?.facility_name) setClinicName(me.clinic.facility_name);
                if (res.registration) {
                    if(!hasDeviceDraft('registration:'+id))setForm(toFormState(res.registration));
                    setCompletedSet(new Set(res.registration.completed_sections));
                }
            } catch (err) {
                if (cancelled) return;
                setLoadError(
                    err instanceof ApiError
                        ? err.message || err.code
                        : 'Could not load this patient.',
                );
            }
        })();
        return () => { cancelled = true; };
    }, [id]);

    function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
        setForm((prev) => ({ ...prev, [key]: value }));
    }

    const allSectionsComplete = useMemo(
        () => REG_SECTION_KEYS.every((k) => completedSet.has(k)),
        [completedSet],
    );

    async function save(submit = false) {
        if (!id) return;
        setSaveError(null);
        if (form.dob && ageFromDob(form.dob) === null) {setSaveError('Enter a valid date of birth that is not in the future.'); return;}
        if (form.anc_details) {const err = validateAncDetails(form.anc_details); if (err) {setSaveError('Check the ANC fields: ' + err.replaceAll('_', ' ')); return;}}
        setSaveNotice(null);
        if (submit && !allSectionsComplete) {
            setSaveError('Mark every section complete before submitting.');
            return;
        }
        setSaving(true);
        try {
            const patch = toPatch(form);
            // Build the section marks diff from the server state + user state.
            const serverMarks = new Set(bundle?.registration?.completed_sections ?? []);
            const marks: Partial<Record<RegSectionKey, boolean>> = {};
            for (const k of REG_SECTION_KEYS) {
                const now = completedSet.has(k);
                const was = serverMarks.has(k);
                if (now !== was) marks[k] = now;
            }
            if (Object.keys(marks).length > 0) patch.section_marks = marks;
            if (submit) patch.submit = true;

            const updated = await receptionApi.updateRegistration(id, patch);
            setBundle((prev) => prev ? { ...prev, registration: updated } : prev);
            void queryClient.invalidateQueries({ queryKey: ['clinic-patients'] });
            void queryClient.invalidateQueries({ queryKey: ['anc-registration', id] });
            setForm(toFormState(updated));
            setCompletedSet(new Set(updated.completed_sections));
            setSaveNotice(submit ? 'Registration submitted.' : offlineState()?'Draft saved on this device. Other staff will see it after sync.':'Draft saved. Your clinic team can find this patient in Patient records and continue the profile.');
        } catch (err) {
            if (err instanceof ApiError) {
                if (err.code === 'child_dob_conflict') {
                    setSaveError('This birth date is linked to a child-health journey. Ask clinical staff to verify an identity/date correction; the existing timeline has been kept.');
                } else if (err.code === 'incomplete_sections') {
                    setSaveError('All 6 sections must be marked complete before submitting.');
                } else {
                    setSaveError(err.message || err.code);
                }
            } else {
                setSaveError('Save failed. Try again.');
            }
        } finally {
            setSaving(false);
        }
    }

    async function addHmo() {
        if (!id || !hmoSelected) return;
        setHmoError(null);
        setHmoBusy(true);
        try {
            const coverage = await receptionApi.upsertHmo(id, {
                hmoProviderId: hmoSelected,
                policyNumber: hmoPolicy,
                planName: hmoPlan,
                memberReference: hmoMember,
            });
            // Replace same-provider row or append.
            setHmoCoverages((prev) => {
                const i = prev.findIndex((c) => c.id === coverage.id);
                if (i >= 0) { const copy = [...prev]; copy[i] = coverage; return copy; }
                return [...prev, coverage];
            });
            setHmoPolicy('');
            setHmoPlan('');
            setHmoMember('');
        } catch (err) {
            setHmoError(
                err instanceof ApiError ? err.message || err.code : 'HMO save failed.',
            );
        } finally {
            setHmoBusy(false);
        }
    }

    async function sendToDepartment() {
        if (!id || !sendDeptId) return;
        setSendError(null);
        setSendNotice(null);
        setSendBusy(true);
        try {
            const activeCard = bundle?.cards.find((c) => c.status === 'active') ?? null;
            const args: Parameters<typeof receptionApi.enqueue>[1] = {
                departmentId: sendDeptId,
            };
            if (activeCard) args.patientCardId = activeCard.id;
            if (sendComplaint.trim()) args.chiefComplaint = sendComplaint.trim();
            await receptionApi.enqueue(id, args);
            const deptName = departments.find((d) => d.id === sendDeptId)?.name ?? 'department';
            setSendNotice(offlineState()?`Arrival saved on this device for ${deptName}. The station will see it after sync.`:`Sent to ${deptName}.`);
            setSendComplaint('');
        } catch (err) {
            setSendError(
                err instanceof ApiError ? err.message || err.code : 'Could not send to department.',
            );
        } finally {
            setSendBusy(false);
        }
    }

    function toggleSection(key: RegSectionKey, next: boolean) {
        setCompletedSet((prev) => {
            const copy = new Set(prev);
            if (next) copy.add(key); else copy.delete(key);
            return copy;
        });
    }

    if (loadError) {
        return (
            <div className="space-y-4">
                <button
                    type="button"
                    className="text-slate-500 text-sm hover:text-slate-700"
                    onClick={() => navigate('/reception')}
                >
                    ← Back to Reception
                </button>
                <div className="pm-card p-4 border-rose-200 bg-rose-50">
                    <div className="text-rose-800 text-sm">{loadError}</div>
                </div>
            </div>
        );
    }

    if (!bundle) {
        return (
            <div className="text-slate-500 text-sm py-6 text-center">Loading…</div>
        );
    }

    const { patient, cards } = bundle;
    const providerLabel = (id: string) =>
        hmoProviders.find((p) => p.id === id)?.display_name
        ?? hmoProviders.find((p) => p.id === id)?.short_name
        ?? '(unknown provider)';

    return (
        <div className="space-y-6">
            <div>
                <button
                    type="button"
                    className="text-slate-500 text-sm hover:text-slate-700"
                    onClick={() => navigate('/reception')}
                >
                    ← Back to Reception
                </button>
                <div className="flex items-start justify-between gap-3 mt-2">
                    <div>
                        <h1 className="text-slate-900 text-2xl font-semibold">
                            {patient.full_name || '(No name)'}
                        </h1>
                        <p className="text-slate-500 text-sm mt-1">
                            {patient.phone_e164} · {patient.identity_tier}
                        </p>
                    </div>
                    <button
                        type="button"
                        className="pm-btn-outline text-sm whitespace-nowrap"
                        onClick={() => openRegistrationSummaryWindow({
                            clinicName,
                            patient: {
                                full_name: patient.full_name,
                                phone_e164: patient.phone_e164,
                                identity_tier: patient.identity_tier,
                            },
                            cards,
                            registration: bundle.registration,
                            hmo_coverages: hmoCoverages,
                            hmoProviderName: providerLabel,
                        })}
                    >
                        Print registration
                    </button>
                </div>
            </div>

            {/* ─── Cards summary ─────────────────────────────────────────── */}
            <div className="pm-card p-4 space-y-3">
                <div className="text-slate-700 text-sm font-semibold">Cards at this clinic</div>
                {cards.length === 0 ? (
                    <div className="text-slate-500 text-sm">No cards.</div>
                ) : (
                    <ul className="space-y-2">
                        {cards.map((c) => (
                            <li key={c.id} className="flex items-center justify-between gap-3">
                                <div>
                                    <div className="text-slate-900 text-sm font-medium">
                                        {PATIENT_CARD_TYPE_LABELS[c.card_type]}
                                    </div>
                                    <div className="text-slate-500 text-xs">
                                        Opened {new Date(c.opened_at).toLocaleDateString()}
                                        {c.hospital_number ? ` · #${c.hospital_number}` : ''}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {c.status === 'active' && (
                                        <button
                                            type="button"
                                            className="pm-btn-ghost text-xs"
                                            onClick={() => openPatientCardWindow({
                                                clinicName,
                                                patient: {
                                                    full_name: patient.full_name,
                                                    phone_e164: patient.phone_e164,
                                                },
                                                card: c,
                                                registration: bundle.registration,
                                            })}
                                        >
                                            Print card
                                        </button>
                                    )}
                                    <span
                                        className={
                                            'text-xs uppercase tracking-wide px-2 py-0.5 rounded '
                                            + (c.status === 'active'
                                                ? 'bg-emerald-50 text-emerald-700'
                                                : 'bg-slate-100 text-slate-500')
                                        }
                                    >
                                        {c.status}
                                    </span>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* ─── Shared instructions (Phase 2b) ──────────────────────── */}
            <SharedInstructionsPanel patientId={patient.id} />
            <ClinicalNotesPanel patientId={patient.id} title="Clinical note history" />

            {/* ─── Send to department (Phase 2a) ───────────────────────── */}
            <div className="pm-card p-4 space-y-3">
                <div className="text-slate-700 text-sm font-semibold">Send to department</div>
                <p className="text-slate-500 text-xs">
                    Add this patient to a clinic queue. Idempotent — pressing twice does not duplicate.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <select
                        className="pm-input"
                        value={sendDeptId}
                        onChange={(e) => setSendDeptId(e.target.value)}
                        disabled={sendBusy || departments.length === 0}
                    >
                        <option value="">— Select department —</option>
                        {departments.map((d) => (
                            <option key={d.id} value={d.id}>
                                {d.name}{d.short_name ? ` (${d.short_name})` : ''}
                            </option>
                        ))}
                    </select>
                    <input
                        className="pm-input"
                        placeholder="Chief complaint (optional)"
                        value={sendComplaint}
                        onChange={(e) => setSendComplaint(e.target.value)}
                        disabled={sendBusy}
                        maxLength={500}
                    />
                </div>
                {sendError && <div className="text-rose-700 text-xs">{sendError}</div>}
                {sendNotice && <div className="text-emerald-700 text-xs">{sendNotice}</div>}
                <button
                    type="button"
                    className="pm-btn-primary"
                    disabled={sendBusy || !sendDeptId}
                    onClick={() => void sendToDepartment()}
                >
                    {sendBusy ? 'Sending…' : 'Send to department'}
                </button>
            </div>

            {bundle.cards.some(c => ['family','corporate','gynae'].includes(c.card_type) && c.status === 'active') && <h2 className="text-slate-700 font-semibold">Beneficiary details</h2>}
            {(bundle.cards.some(c => c.card_type === 'anc' && c.status === 'active') || form.anc_details) && <AncRegistrationFields value={form.anc_details} onChange={v => setField('anc_details', v)} />}

            {/* ─── Section A — Personal ─────────────────────────────────── */}
            <Section
                title={SECTION_LABELS.personal}
                completed={completedSet.has('personal')}
                onToggleComplete={(v) => toggleSection('personal', v)}
            >
                <DateOfBirthFields dob={form.dob ?? ''} estimatedAge={form.estimated_age_years ?? ''}
                    onDobChange={dob => setForm(prev => ({...prev,dob,estimated_age_years:dob ? String(ageFromDob(dob) ?? '') : '',estimated_age_as_of:dob ? todayDate() : ''}))}
                    onAgeChange={age => setForm(prev=>({...prev,estimated_age_years:age,estimated_age_as_of:age ? todayDate() : ''}))} />
                <Field label="Gender">
                    <select className="pm-input" value={form.gender ?? ''} onChange={(e) => setField('gender', e.target.value as FormState['gender'])}>
                        <option value="">—</option>
                        {OPTS_GENDER.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                </Field>
                <Field label="Clinical sex (if different)">
                    <input className="pm-input" value={form.clinical_sex ?? ''} onChange={(e) => setField('clinical_sex', e.target.value)} />
                </Field>
                <Field label="Marital status">
                    <select className="pm-input" value={form.marital_status ?? ''} onChange={(e) => setField('marital_status', e.target.value as FormState['marital_status'])}>
                        <option value="">—</option>
                        {OPTS_MARITAL.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                </Field>
            </Section>

            {/* ─── Section B — Contact ───────────────────────────────────── */}
            <Section
                title={SECTION_LABELS.contact}
                completed={completedSet.has('contact')}
                onToggleComplete={(v) => toggleSection('contact', v)}
            >
                <Field label="Address" full>
                    <textarea className="pm-input" rows={2} value={form.address ?? ''} onChange={(e) => setField('address', e.target.value)} />
                </Field>
                <Field label="Primary phone (E.164)">
                    <input className="pm-input" placeholder="+2348012345678" value={form.phone_e164_primary ?? ''} onChange={(e) => setField('phone_e164_primary', e.target.value)} />
                </Field>
                <Field label="Alt phone (E.164)">
                    <input className="pm-input" placeholder="+2348012345678" value={form.phone_e164_alt ?? ''} onChange={(e) => setField('phone_e164_alt', e.target.value)} />
                </Field>
                <Field label="Email">
                    <input className="pm-input" type="email" value={form.email ?? ''} onChange={(e) => setField('email', e.target.value)} />
                </Field>
                <Field label="Preferred contact channel">
                    <select className="pm-input" value={form.preferred_contact_channel ?? ''} onChange={(e) => setField('preferred_contact_channel', e.target.value)}>
                        <option value="">—</option>
                        {OPTS_CHANNEL.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                </Field>
            </Section>

            {/* ─── Section C — Socio-demographic ─────────────────────────── */}
            <Section
                title={SECTION_LABELS.socio}
                completed={completedSet.has('socio')}
                onToggleComplete={(v) => toggleSection('socio', v)}
            >
                <Field label="Occupation">
                    <input className="pm-input" value={form.occupation ?? ''} onChange={(e) => setField('occupation', e.target.value)} />
                </Field>
                <Field label="State of origin">
                    <input className="pm-input" value={form.state_of_origin ?? ''} onChange={(e) => setField('state_of_origin', e.target.value)} />
                </Field>
                <Field label="Ethnicity">
                    <input className="pm-input" value={form.ethnicity ?? ''} onChange={(e) => setField('ethnicity', e.target.value)} />
                </Field>
                <Field label="Religion">
                    <input className="pm-input" value={form.religion ?? ''} onChange={(e) => setField('religion', e.target.value)} />
                </Field>
            </Section>

            {/* ─── Section D — Social history ─────────────────────────────── */}
            <Section
                title={SECTION_LABELS.social}
                completed={completedSet.has('social')}
                onToggleComplete={(v) => toggleSection('social', v)}
            >
                <Field label="Smoking">
                    <select className="pm-input" value={form.smoking ?? ''} onChange={(e) => setField('smoking', e.target.value as FormState['smoking'])}>
                        <option value="">—</option>
                        {OPTS_SMOKING.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                </Field>
                <Field label="Alcohol">
                    <select className="pm-input" value={form.alcohol ?? ''} onChange={(e) => setField('alcohol', e.target.value as FormState['alcohol'])}>
                        <option value="">—</option>
                        {OPTS_ALCOHOL.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                </Field>
                <Field label="Recreational drugs">
                    <select
                        className="pm-input"
                        value={form.recreational_drugs === null || form.recreational_drugs === undefined ? '' : form.recreational_drugs ? 'yes' : 'no'}
                        onChange={(e) => {
                            const v = e.target.value;
                            setField('recreational_drugs', v === '' ? null : v === 'yes');
                        }}
                    >
                        <option value="">—</option>
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                    </select>
                </Field>
                <Field label="If yes, details">
                    <input className="pm-input" value={form.recreational_drugs_detail ?? ''} onChange={(e) => setField('recreational_drugs_detail', e.target.value)} />
                </Field>
                <Field label="Living situation">
                    <select className="pm-input" value={form.living_situation ?? ''} onChange={(e) => setField('living_situation', e.target.value as FormState['living_situation'])}>
                        <option value="">—</option>
                        {OPTS_LIVING.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                </Field>
                <Field label="Living situation detail">
                    <input className="pm-input" value={form.living_situation_detail ?? ''} onChange={(e) => setField('living_situation_detail', e.target.value)} />
                </Field>
            </Section>

            {/* ─── Section E — Medical history ────────────────────────────── */}
            <Section
                title={SECTION_LABELS.medical}
                completed={completedSet.has('medical')}
                onToggleComplete={(v) => toggleSection('medical', v)}
            >
                <Field label="Genotype">
                    <select className="pm-input" value={form.genotype ?? ''} onChange={(e) => setField('genotype', e.target.value as FormState['genotype'])}>
                        <option value="">—</option>
                        {OPTS_GENOTYPE.map((g) => <option key={g} value={g}>{g.toUpperCase()}</option>)}
                    </select>
                </Field>
                <Field label="Blood group (ABO)">
                    <select className="pm-input" value={form.blood_group_abo ?? ''} onChange={(e) => setField('blood_group_abo', e.target.value as FormState['blood_group_abo'])}>
                        <option value="">—</option>
                        {OPTS_ABO.map((g) => <option key={g} value={g}>{g.toUpperCase()}</option>)}
                    </select>
                </Field>
                <Field label="Rh factor">
                    <select className="pm-input" value={form.blood_group_rh ?? ''} onChange={(e) => setField('blood_group_rh', e.target.value as FormState['blood_group_rh'])}>
                        <option value="">—</option>
                        {OPTS_RH.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                </Field>
                <Field label="Allergy status">
                    <select className="pm-input" value={form.allergy_status ?? ''} onChange={(e) => setField('allergy_status', e.target.value as FormState['allergy_status'])}>
                        <option value="">—</option>
                        {OPTS_ALLERGY.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                </Field>
                <Field label="Allergies — substance + reaction" full>
                    <textarea className="pm-input" rows={2} value={form.allergies_detail ?? ''} onChange={(e) => setField('allergies_detail', e.target.value)} />
                </Field>
                <Field label="Chronic conditions" full>
                    <textarea className="pm-input" rows={2} value={form.chronic_conditions_detail ?? ''} onChange={(e) => setField('chronic_conditions_detail', e.target.value)} />
                </Field>
                <Field label="Current medicines" full>
                    <textarea className="pm-input" rows={2} value={form.current_medicines_detail ?? ''} onChange={(e) => setField('current_medicines_detail', e.target.value)} />
                </Field>
                <Field label="Surgical history" full>
                    <textarea className="pm-input" rows={2} value={form.surgical_history_detail ?? ''} onChange={(e) => setField('surgical_history_detail', e.target.value)} />
                </Field>
            </Section>

            {/* ─── Section F — Next of kin ─────────────────────────────────── */}
            <Section
                title={SECTION_LABELS.nok}
                completed={completedSet.has('nok')}
                onToggleComplete={(v) => toggleSection('nok', v)}
            >
                <Field label="Next of kin — name">
                    <input className="pm-input" value={form.nok_name ?? ''} onChange={(e) => setField('nok_name', e.target.value)} />
                </Field>
                <Field label="Next of kin — phone (E.164)">
                    <input className="pm-input" value={form.nok_phone_e164 ?? ''} onChange={(e) => setField('nok_phone_e164', e.target.value)} />
                </Field>
                <Field label="Relationship">
                    <input className="pm-input" value={form.nok_relationship ?? ''} onChange={(e) => setField('nok_relationship', e.target.value)} />
                </Field>
                <Field label="Next of kin — address" full>
                    <textarea className="pm-input" rows={2} value={form.nok_address ?? ''} onChange={(e) => setField('nok_address', e.target.value)} />
                </Field>
                <Field label="Alt emergency contact — name">
                    <input className="pm-input" value={form.alt_emergency_contact_name ?? ''} onChange={(e) => setField('alt_emergency_contact_name', e.target.value)} />
                </Field>
                <Field label="Alt emergency contact — phone">
                    <input className="pm-input" value={form.alt_emergency_contact_phone ?? ''} onChange={(e) => setField('alt_emergency_contact_phone', e.target.value)} />
                </Field>
            </Section>

            {/* ─── Save / Submit controls ───────────────────────────────────── */}
            <div className="pm-card p-4 space-y-3">
                {saveError && <div className="text-rose-700 text-sm">{saveError}</div>}
                {saveNotice && <div className="text-emerald-700 text-sm">{saveNotice}</div>}
                <div className="flex gap-3">
                    <button
                        type="button"
                        className="pm-btn-outline"
                        disabled={saving}
                        onClick={() => void save(false)}
                    >
                        {saving ? 'Saving…' : 'Save draft'}
                    </button>
                    <button
                        type="button"
                        className="pm-btn-primary flex-1"
                        disabled={saving || !allSectionsComplete}
                        onClick={() => void save(true)}
                    >
                        {saving ? 'Submitting…' : (bundle.registration?.is_draft === false ? 'Save (already submitted)' : 'Submit registration')}
                    </button>
                </div>
                {!allSectionsComplete && (
                    <p className="text-slate-500 text-xs">
                        Mark every section complete to enable submit. {completedSet.size}/6 done.
                    </p>
                )}
            </div>

            {/* ─── Section H — HMO ───────────────────────────────────────── */}
            <div className="pm-card p-4 space-y-3">
                <div className="text-slate-700 text-sm font-semibold">Section H — HMO coverage</div>
                {hmoCoverages.length === 0 ? (
                    <div className="text-slate-500 text-sm">No HMO coverage yet.</div>
                ) : (
                    <ul className="space-y-2">
                        {hmoCoverages.map((h) => (
                            <HmoCoverageRow
                                key={h.id}
                                coverage={h}
                                providerLabel={providerLabel}
                                patientId={id ?? ''}
                                onUpdated={(next) => setHmoCoverages((prev) => {
                                    const i = prev.findIndex((c) => c.id === next.id);
                                    if (i < 0) return prev;
                                    const copy = [...prev];
                                    copy[i] = next;
                                    return copy;
                                })}
                            />
                        ))}
                    </ul>
                )}

                <div className="pt-2 border-t border-slate-100 space-y-2">
                    <div className="text-slate-600 text-xs font-semibold">Add / update coverage</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <select
                            className="pm-input"
                            value={hmoSelected}
                            onChange={(e) => setHmoSelected(e.target.value)}
                            disabled={hmoBusy}
                        >
                            <option value="">— Select HMO provider —</option>
                            {hmoProviders.map((p) => (
                                <option key={p.id} value={p.id}>{p.display_name}</option>
                            ))}
                        </select>
                        <input className="pm-input" placeholder="Policy number" value={hmoPolicy} onChange={(e) => setHmoPolicy(e.target.value)} disabled={hmoBusy} />
                        <input className="pm-input" placeholder="Plan name" value={hmoPlan} onChange={(e) => setHmoPlan(e.target.value)} disabled={hmoBusy} />
                        <input className="pm-input" placeholder="Member / staff reference" value={hmoMember} onChange={(e) => setHmoMember(e.target.value)} disabled={hmoBusy} />
                    </div>
                    {hmoError && <div className="text-rose-700 text-xs">{hmoError}</div>}
                    <button
                        type="button"
                        className="pm-btn-outline"
                        disabled={hmoBusy || !hmoSelected}
                        onClick={() => void addHmo()}
                    >
                        {hmoBusy ? 'Saving…' : 'Save HMO coverage'}
                    </button>
                    <p className="text-slate-400 text-xs">
                        New rows start as <em>unverified</em>. Receptionist or doctor can mark
                        each coverage as verified / rejected / pending on the row above.
                    </p>
                </div>
            </div>
        </div>
    );
}

// ─── HMO row with verify controls — Phase 3.c₂ (2026-10-04) ────────────
//
// Receptionist (or doctor/admin) marks each coverage verified / pending /
// rejected after calling the HMO. Server stamps verified_by_clinician_id +
// verified_at and writes an audit row via the reception router.

function HmoCoverageRow({
    coverage,
    providerLabel,
    patientId,
    onUpdated,
}: {
    coverage: PatientHmoCoverage;
    providerLabel: (id: string) => string;
    patientId: string;
    onUpdated: (next: PatientHmoCoverage) => void;
}) {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    async function setStatus(status: 'pending' | 'verified' | 'rejected') {
        if (!patientId || busy) return;
        let authorizationReference: string | null | undefined = undefined;
        if (status === 'verified') {
            const entered = window.prompt('Authorization reference (optional):',
                coverage.authorization_reference ?? '');
            if (entered === null) return;
            authorizationReference = entered.trim() || null;
        }
        setBusy(true);
        setError(null);
        try {
            const next = await receptionApi.verifyHmo(patientId, coverage.id, {
                status,
                ...(authorizationReference !== undefined
                    ? { authorization_reference: authorizationReference }
                    : {}),
            });
            onUpdated(next);
        } catch (err) {
            setError(err instanceof ApiError ? err.message || err.code : 'Update failed.');
        } finally {
            setBusy(false);
        }
    }

    const status = coverage.verification_status;
    return (
        <li className="pm-card p-3 space-y-2">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-slate-900 text-sm font-medium">
                        {providerLabel(coverage.hmo_provider_id)}
                    </div>
                    <div className="text-slate-500 text-xs">
                        {coverage.policy_number ? `Policy ${coverage.policy_number}` : 'No policy #'}
                        {coverage.plan_name ? ` · ${coverage.plan_name}` : ''}
                    </div>
                    {coverage.authorization_reference && (
                        <div className="text-slate-500 text-xs">
                            Auth: {coverage.authorization_reference}
                        </div>
                    )}
                </div>
                <span
                    className={
                        'text-xs uppercase tracking-wide px-2 py-0.5 rounded shrink-0 '
                        + (status === 'verified'
                            ? 'bg-emerald-50 text-emerald-700'
                            : status === 'rejected'
                                ? 'bg-rose-50 text-rose-700'
                                : status === 'pending'
                                    ? 'bg-amber-50 text-amber-700'
                                    : 'bg-slate-100 text-slate-500')
                    }
                >
                    {status}
                </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    className="pm-btn-ghost text-xs"
                    disabled={busy || status === 'pending'}
                    onClick={() => void setStatus('pending')}
                >
                    Mark pending
                </button>
                <button
                    type="button"
                    className="pm-btn-ghost text-xs text-emerald-700"
                    disabled={busy || status === 'verified'}
                    onClick={() => void setStatus('verified')}
                >
                    Verify
                </button>
                <button
                    type="button"
                    className="pm-btn-ghost text-xs text-rose-700"
                    disabled={busy || status === 'rejected'}
                    onClick={() => void setStatus('rejected')}
                >
                    Reject
                </button>
                {busy && <span className="text-slate-400 text-xs">Saving…</span>}
            </div>
            {error && <div className="text-rose-700 text-xs">{error}</div>}
        </li>
    );
}
